import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
export interface ApiErrorItem {
  errorCode: number;
  errorKey: string;
  language: string;
  message: string;
  statusCode: number;
}

interface ErrorLike {
  code?: unknown;
  error?: unknown;
  errorCode?: unknown;
  message?: unknown;
  response?: { data?: unknown };
}

@Injectable({
  providedIn: "root",
})
export class ErrorMessageService {
  private errorMap = new Map<string, Map<number, string>>();
  private currentLang = "vi";

  apiUrl!: string;
  apiV!: string;

  constructor(private http: HttpClient) {
    this.apiUrl = environment.apiURL;
    this.apiV = environment.version;
  }

  async loadErrors(lang: "vi" | "en"): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ data: ApiErrorItem[] }>(
          `${this.apiUrl}/api/${this.apiV}/ErrorMessage?Language=${lang}`
        )
      );

      if (res?.data?.length) {
        const langMap = new Map<number, string>();
        res.data.forEach((err) => langMap.set(err.errorCode, err.message));
        this.errorMap.set(lang, langMap);
      }
    } catch (e) {
      console.error(`Không thể load error list (${lang}):`, e);
    }
  }

  async setLanguage(lang: "vi" | "en"): Promise<void> {
    this.currentLang = lang;
    if (!this.errorMap.has(lang)) {
      await this.loadErrors(lang);
    }
  }

  getErrorCode(error: unknown): number | null {
    const source = error as ErrorLike | null | undefined;
    const body = (source?.response?.data ?? source?.error ?? source) as ErrorLike | null | undefined;
    const detail = body?.error as { code?: unknown } | null | undefined;
    const rawCode = detail?.code ?? body?.code ?? body?.errorCode;

    if (typeof rawCode === "number") return rawCode;
    if (typeof rawCode === "string" && rawCode.trim() !== "") {
      const parsed = Number(rawCode);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  resolveErrorMessage(error: unknown, fallback: string): string {
    const source = error as ErrorLike | null | undefined;
    const body = (source?.response?.data ?? source?.error ?? source) as ErrorLike | null | undefined;
    const code = this.getErrorCode(error);
    const mapped = this.getMessage(code);
    if (mapped) return mapped;

    if (typeof body?.message === "string" && body.message.trim() !== "") {
      return body.message;
    }

    return fallback;
  }

  getMessage(errorCode: number | null | undefined): string | null {
    if (errorCode === null || errorCode === undefined) return null;
    return this.errorMap.get(this.currentLang)?.get(errorCode) ?? null;
  }
}
